import { memo, Suspense, useMemo, type FC } from 'react';

import type { IBlock } from '@dcentralexpedientedigital/fe-shared/types/config.interface';

import { useProcessedBlocks } from '../hooks/use-processed-blocks';
import { componentRegistry } from '../registry';
import { hasSpacing, isComponentBlock } from '../utils/is-component-block';
import { isLayoutBlock } from '../utils/is-layout-block';

export interface IBlockRendererProps {
    blocks: IBlock[] | IBlock;
    hasLayout?: boolean;
}

const BLOCK_LAYOUT_COMPONENT =
    '@dcentralexpedientedigital/fe-ui/components/block-layout/block-layout';

export const BlockRenderer: FC<IBlockRendererProps> = memo(({ blocks, hasLayout = false }) => {
    const allBlocks = useMemo(() => (Array.isArray(blocks) ? blocks : [blocks]), [blocks]);
    const resolvedBlocks = useProcessedBlocks(allBlocks);

    const memoizedBlocks = useMemo(
        () =>
            resolvedBlocks.map((block, i) => {
                const BlockLayout = componentRegistry[BLOCK_LAYOUT_COMPONENT];

                if (isComponentBlock(block)) {
                    const Component = componentRegistry[block.name];

                    // TODO: Use component metadata to figure out what props should be
                    //       treated as block rendered components
                    const children =
                        'children' in block.properties ? (
                            <BlockRenderer blocks={block.properties.children as IBlock[]} />
                        ) : undefined;

                    const normalizedProps = Object.fromEntries(
                        Object.entries(block.properties).map(([key, value]) => [
                            key,
                            value === 'true' ? true : value === 'false' ? false : value,
                        ]),
                    );

                    const element = (
                        <Suspense key={i} fallback={null}>
                            <Component {...normalizedProps} children={children} />
                        </Suspense>
                    );

                    if (!hasLayout && hasSpacing(block)) {
                        return (
                            <BlockLayout key={i} spacing={block.spacing}>
                                {element}
                            </BlockLayout>
                        );
                    }

                    return element;
                }

                if (isLayoutBlock(block)) {
                    const { components, ...blockLayoutConfig } = block;
                    // lazy load the block layout component wrapping the components
                    return (
                        <BlockLayout key={i} {...blockLayoutConfig}>
                            {components.map((componentBlock, index) => (
                                <BlockRenderer key={index} blocks={[componentBlock]} hasLayout />
                            ))}
                        </BlockLayout>
                    );
                }

                return <div key={i}>Layout not supported</div>;
            }),
        [resolvedBlocks],
    );

    return memoizedBlocks;
});
